import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../components/CustomButton";
import useCustomStyles from "../../hooks/useCustomStyles";
import { selectState } from "../../store/slices/themeSlice";
import { AppDispatch, RootState } from "../../store/store";
import {
  ContractType,
  getBusinesses,
} from "../../store/actions/accessCodeActions";
import { fetchBusinesses, getDashboardTheme, getSelectedBusinessDashboardTheme } from "../../store/actions/businessActions";
import TableComponent from "../clients/components/fff";
import DynamicTable from "../clients/components/CustomTable"; // Adjust path as needed
import {
  BUSINESS_COLUMNS,
  CONTRACT_COLUMNS,
  withDescriptions,
} from "../../utils/columnDescriptions";
import { ClientType } from "../../store/slices/clientsSlice";
import { accessCodesStyles } from "../../assets/styles/accessCodesStyles";
import HeaderTabs from "../clients/components/HeaderTabs";
import { useNavigate } from "react-router-dom";
import { ErrorTriage } from "../../api/awsApis";

export interface ContractFormType {
  business_client: string | undefined;
  contract_ref: string | undefined;
  useLimit: number | undefined;
  expire_at: string | Date | undefined;
  id?: string | undefined;
}

const AddContractForm: React.FC = () => {
  const { theme } = useSelector(selectState);
  const { businesses: accessCodeBusinesses } = useSelector(
    (state: RootState) => state.accessCode_slice
  );
  const {
    businesses,
    loading: businessesLoading,
    page,
    limit,
    total,
  } = useSelector((state: RootState) => state.business_slice);

  const navigate = useNavigate();
  const classes = useCustomStyles(accessCodesStyles, theme);
  const dispatch = useDispatch<AppDispatch>();

  const [selectedTab, setSelectedTab] = useState("businesses");
  const [isSearching, setIsSearching] = useState(false);

  const tabs = [
    { value: "businesses", label: "Businesses" }, // ✅ New tab
    { value: "white_labeling", label: "Contracts" },
    { value: "referrals", label: "Referrals" },
  ];

  const contracts = useSelector(
    (state: RootState) => state.accessCode_slice.contracts
  );
  const contract = contracts || [];

  // Fetch access code businesses on mount
  useEffect(() => {
    if (!accessCodeBusinesses) {
      dispatch(getBusinesses({ type: "" }));
    }
  }, []);

  // Fetch businesses when businesses tab is selected
  useEffect(() => {
    if (selectedTab === "businesses") {
      dispatch(fetchBusinesses({ page: 1, limit: 100 }));
    }
  }, [selectedTab, dispatch]);

  // ==================== WHITE LABELING CONFIG ====================
  const dropdowns = [
    {
      options: [
        { value: "bassam", key: "1" },
        { value: "hamz", key: "2" },
        { value: "ali", key: "3" },
        { value: "mohammed", key: "4" },
      ],
      initialState: "Type",
    },
    {
      options: [
        { value: "Admin", key: "1" },
        { value: "User", key: "2" },
        { value: "Guest", key: "3" },
      ],
      initialState: "Role",
    },
  ];

  const whiteLabelingColumns = [
    { key: "contract_ref", label: "Contract", sortable: true },
    { key: "expire_at", label: "Validity", sortable: true },
    { key: "useLimit", label: "Total Licences", sortable: false },
    { key: "licence_count", label: "Licences Used", sortable: false },
    { key: "status", label: "Status", sortable: false },
  ];

  // ==================== BUSINESSES CONFIG ====================
  const businessColumns = [
    { key: "name", label: "Name", isSortable: true },
    { key: "code", label: "Code", isSortable: true },
    { key: "type", label: "Type", isSortable: true },
    { key: "enableAdvisors", label: "Advisors", isSortable: false },
    { key: "needClientId", label: "Client ID", isSortable: false },
    { key: "needAdminDashboard", label: "Dashboard", isSortable: false },
   // { key: "themeStatus", label: "Theme", isSortable: false },
  ];

  // Transform business data for table display
  const businessTableData = businesses.map((business) => ({
    id: business.id,
    name: business.name,
    code: business.code,
    type: business.type ,///.charAt(0).toUpperCase() + business.type.slice(1),
    enableAdvisors: business.enable_advisors ? "✅" : "❌",
    needClientId: business.need_client_id ? "✅" : "❌",
    needAdminDashboard: business.need_admin_dashboard ? "✅" : "❌",
   // themeStatus: business.type == 'partner' || business.type == 'owner' ? business.theme_uploaded ? "✅ Uploaded" : "⏳ Pending" : "N/A",
    _original: business,
  }));

  // ==================== HANDLERS ====================
  const handleClientClick = (
    contract: ClientType | ContractType | ErrorTriage
  ) => {
    navigate(`/contractDetails`, { state: { contract } });
  };

  const handleBusinessClick = (rowData: Record<string, any>) => {
    if (!rowData.id) return; // Safety check
    console.log("Clicked business:", rowData);
    if(rowData._original.type === 'partner' || rowData._original.type === 'owner') {
     dispatch(getSelectedBusinessDashboardTheme({ businessId: rowData.id }));
    }
    navigate(`/businesses/${rowData.id}`);

  };

  const handleBusinessSearch = async (searchTerm: string) => {
    setIsSearching(true);
    await dispatch(fetchBusinesses({ page: 1, limit: 10, search: searchTerm }));
    setIsSearching(false);
  };

  const handleBusinessPageChange = (newPage: number) => {
    dispatch(fetchBusinesses({ page: newPage, limit }));
  };

  const handleNavigate = () => {
    navigate("/addNewContract");
  };

  const handleAddNewWhiteLabel = () => {
    navigate("/addNewWhiteLabel");
  };

  const handleAddNewBusiness = () => {
    navigate("/businesses/create");
  };

  return (
    <div className={classes.container}>
      <div className={classes.tableContainer}>
        <h2 className={classes.header}>BUSINESS CLIENTS</h2>

        <div className={classes.buttonContainer}>
            
          {selectedTab === "businesses" ? (
             <CustomButton
             title={"Add New White Label"}
             handleClick={handleAddNewWhiteLabel}
             name={"Add-New-White-Label"}
             loading={false}
           />
          ): (<CustomButton
            title={"Add New Contract"}
            handleClick={handleNavigate}
            name={"Add-New-Contract"}
            loading={false}
          />)}
        </div>
      </div>
     
      <HeaderTabs
        tabs={tabs}
        selectedTab={selectedTab}
        onSelect={setSelectedTab}
      />
      <div className={classes.tabContainer}>
      {/* White Labeling Tab */}
      {selectedTab === "white_labeling" && (
        <TableComponent
          dropdowns={dropdowns}
          columns={withDescriptions(whiteLabelingColumns, CONTRACT_COLUMNS)}
          data={contract.map((contract) => ({
            ...contract,
            contract,
          }))}
          handleNavigate={handleClientClick}
        />
      )}

      {/* Businesses Tab */}
      {selectedTab === "businesses" && (
        <>
          {businessesLoading && !isSearching ? (
            <div style={{ textAlign: "center", padding: 40, minHeight: '100%', backgroundColor:'white' }}>Loading...</div>
          ) : (
            <DynamicTable
              columns={withDescriptions(businessColumns, BUSINESS_COLUMNS)}
              data={businessTableData}
              onRowClick={handleBusinessClick}
              pagination={true}
              currentPage={page}
              totalPages={total}
              onPageChange={handleBusinessPageChange}
              itemsPerPage={limit}
              onSearch={handleBusinessSearch}
              isSearching={isSearching}
            />
          )}
        </>
      )}

      {/* Referrals Tab */}
      {selectedTab === "referrals" && (
        <div style={{ textAlign: "center", padding: 40, color: "#666", backgroundColor: 'white', minHeight: '100%' }}>
          Referrals content coming soon...
        </div>
      )}
      </div>
    </div>
  );
};

export default AddContractForm;